import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Container,
  Card,
  CardContent,
  Grid,
  Divider,
  Chip,
} from '@mui/material';
import {
  Send as SendIcon,
  Help as HelpIcon,
  Email as EmailIcon,
  Support as SupportIcon,
} from '@mui/icons-material';
import { helpService } from '../services/helpService';

const HELP_CATEGORIES = ['Technical Issues', 'Feature Requests', 'Documentation', 'Bug Reports', 'General Questions'];

const HelpCenter = () => {
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');

  const handleCategoryClick = (category) => {
    const isSame = selectedCategory === category;
    setSelectedCategory(isSame ? '' : category);
    setComment((prev) => {
      const withoutPrefix = prev.replace(/^\[[^\]]+\]\s*/, '');
      return isSame ? withoutPrefix : `[${category}] ${withoutPrefix}`;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!comment.trim()) {
      setError('Please enter a comment before sending.');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      await helpService.sendHelpRequest(comment);
      setSuccess(true);
      setComment('');
      setSelectedCategory('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send help request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <HelpIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
        <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
          Help Center
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
          Need assistance? Send us a message and we'll get back to you.
        </Typography>
      </Box>

      <Grid container spacing={4}>
        {/* Help Request Form */}
        <Grid item xs={12} md={8}>
          <Paper variant="outlined" sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <EmailIcon sx={{ mr: 2, color: 'primary.main' }} />
              <Typography variant="h5" component="h2">
                Send Help Request
              </Typography>
            </Box>

            {success && (
              <Alert severity="success" sx={{ mb: 3 }}>
                Your help request has been sent successfully! We'll get back to you soon.
              </Alert>
            )}

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <TextField
                fullWidth
                multiline
                rows={6}
                label="Describe your issue or question"
                placeholder="Please provide details about your issue, question, or feedback. Be as specific as possible to help us assist you better."
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                variant="outlined"
                sx={{ mb: 3 }}
                disabled={loading}
              />

              <Button
                type="submit"
                variant="contained"
                size="large"
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                disabled={loading || !comment.trim()}
                sx={{ 
                  borderRadius: 2,
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem'
                }}
              >
                {loading ? 'Sending...' : 'Send Help Request'}
              </Button>
            </form>
          </Paper>
        </Grid>

        {/* Help Information */}
        <Grid item xs={12} md={4}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <SupportIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" component="h3">
                  What we can help with
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                Click a category to tag your message
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {HELP_CATEGORIES.map((category) => (
                  <Chip
                    key={category}
                    label={category}
                    size="small"
                    clickable
                    variant={selectedCategory === category ? 'filled' : 'outlined'}
                    color="primary"
                    onClick={() => handleCategoryClick(category)}
                  />
                ))}
              </Box>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" component="h3" gutterBottom>
                Response Time
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="body2" color="text.secondary" paragraph>
                We typically respond to help requests within 24-48 hours during business days.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                For urgent technical issues, please include "URGENT" in your message.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default HelpCenter;




